import * as Sentry from '@sentry/vue'
import { reportScriptLoadError } from '@/utils/reportScriptLoadError'
import { COOKIE_OPTIONS } from '@/utils/constants/cookies'
import { useUserStore } from '@/stores/User.store'

declare global {
  interface Window {
    ScarabQueue?: any[]
  }
}

export default defineNuxtPlugin(() => {
  const gdprCookies = useCookie<string>('gdpr_settings', { ...COOKIE_OPTIONS, watch: true })
  const runtimeConfig = useRuntimeConfig()

  const sapID = runtimeConfig.public.sapID as string | undefined

  if (!sapID) {
    console.warn('SAP ID is not defined')
    return
  }

  const scarabScriptSrc = `https://cdn.scarabresearch.com/js/${sapID}/scarab-v2.js`
  const userStore = useUserStore()

  const addScarabBreadcrumb = (message: string, data: Record<string, unknown> = {}) => {
    try {
      Sentry.addBreadcrumb({
        category: 'scarab',
        message,
        level: 'info',
        data: {
          scriptSrc: scarabScriptSrc,
          ...data
        }
      })
    } catch (error) {
      console.warn('Failed to add Scarab breadcrumb in Sentry', error)
    }
  }

  const reportScarabLoadError = (error: unknown) => {
    addScarabBreadcrumb('script-load-failed', {
      errorType: error instanceof Event ? error.type : typeof error
    })

    reportScriptLoadError({
      error,
      integration: 'scarab',
      message: 'scarab_script_load_failed',
      scriptSrc: scarabScriptSrc
    })
  }

  const { load, unload } = useScriptTag(
    scarabScriptSrc,
    () => {
      if (userStore.isUserLogged && userStore.userData.email) {
        globalThis.window.ScarabQueue?.push(['setEmail', userStore.userData.email])
      }
      globalThis.window.ScarabQueue?.push(['go'])
      addScarabBreadcrumb('script-loaded')
    },
    {
      manual: true,
      attrs: { id: 'scarab-js-api' }
    }
  )

  const hasConsent = () => {
    const raw = gdprCookies.value || ''
    const parts = new Set(
      raw
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean)
    )
    return parts.has('web') || parts.has('adv')
  }

  const removeScripts = async () => {
    if (document.getElementById('scarab-js-api')) unload()

    globalThis.window.ScarabQueue = []
  }

  const init = async () => {
    globalThis.window.ScarabQueue = globalThis.window.ScarabQueue || []
    if (hasConsent()) {
      try {
        await load()
      } catch (error) {
        reportScarabLoadError(error)
        await removeScripts()
      }
    } else {
      await removeScripts()
      addScarabBreadcrumb('script-removed:no-consent')
    }
  }

  void init()
  watch(gdprCookies, () => {
    void init()
  })
})
